import React, { useState, useEffect } from 'react';
import { Typography, Button, Table, Input, Form, DatePicker, Space, Select, Tooltip } from 'antd';
import { ClearOutlined } from '@ant-design/icons';

import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './fixLog.css';
import FIXMessageLogService from '../../services/FIXMessageLogService';
import { convertToLocal } from '../../components/Functions/convertToLocal';
import dayjs from 'dayjs';
import { objectToQueryString } from '../../util/Common';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const FixMessageLog = () => {
  const location = useLocation();
  const [messageLogList, setMessageLogList] = useState([]);
  const navigate = useNavigate();
  const { showNotification } = useAuth();
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [form] = Form.useForm();
  const queryParams = new URLSearchParams(location.search);

  const startTimestamp = Number(queryParams.get('startDate')) || null;
  const endTimestamp = Number(queryParams.get('endDate')) || null;
  const messageType = queryParams.get('messageType') || null;

  const page = Number(queryParams.get('page')) || 1;
  const size = Number(queryParams.get('size')) || 10;

  const startDate = startTimestamp ? dayjs(startTimestamp * 1000) : null;
  const endDate = endTimestamp ? dayjs(endTimestamp * 1000) : null;

  const loadData = async () => {
    const payload = {
      startDate: startTimestamp,
      endDate: endTimestamp,
      messageType: messageType,
      page: page,
      size: size,
    };

    setLoading(true);
    const result = await FIXMessageLogService.getFIXMessageLog(payload);
    setLoading(false);
    if (result.success) {
      setMessageLogList(result.fixMessageLogList);
      console.log(result.fixMessageLogList);
      setTotalCount(result.totalCount);
    } else {
      showNotification('error', result.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [location.search]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const formLayout = windowWidth <= 1024 ? 'vertical' : 'inline';

  const onFinish = async formValues => {
    let startDateTimestamp = null;
    let endDateTimestamp = null;
  
    if (formValues.selectDates && formValues.selectDates[0]) {
      startDateTimestamp = formValues.selectDates[0].startOf('day').valueOf(); 
    }
    if (formValues.selectDates && formValues.selectDates[1]) {

      endDateTimestamp = formValues.selectDates[1].endOf('day').valueOf(); 
    }
    let payload = {
      startDate: startDateTimestamp !== null ? Math.floor(startDateTimestamp / 1000) : null,
      endDate: endDateTimestamp !== null ? Math.floor(endDateTimestamp / 1000) : null,
      messageType: formValues.messageType ? formValues.messageType : null,
      page: 1,
      size: 10,
    };

    const queryString = objectToQueryString(payload);

    navigate(`/reports/fix-message-log?` + queryString);
  };

  const handleRefresh = () => {
    navigate('/reports/fix-message-log');
    form.setFieldsValue({
      selectDates: [null, null],
      messageType: null,
    });
  };

  const columns = [
    {
      title: 'Request ID',
      dataIndex: 'requestId',
      key: 'requestId',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'Message Type',
      dataIndex: 'messageType',
      key: 'messageType',
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
    },
    {
      title: 'Created At',
      dataIndex: 'createAt',
      key: 'createAt',
      render: (_, record) => convertToLocal(record.createAt),
    },
  ];

  const messageTypeList = [
    'Logon',
    'HeartBeat',
    'TradingSessionStatus',
    'Reject',
    'ResendRequest',
    'SequenceReset',
    'Logout',
    'TESTREQUEST',
    'NewOrderRequest',
    'ExecutionReport',
    'OrderCancelRequest',
    'OrderCancelReject',
    'BusinessMessageReject',
  ];

  return (
    <>
      <Title level={4} style={{ marginTop: 0 }}>
        Fix Message Log
      </Title>
      <Form
        onFinish={onFinish}
        form={form}
        className="right-aligned-form"
        layout={formLayout}
        initialValues={{
          selectDates: [startDate, endDate],
          messageType: messageType,
        }}
      >
        <Form.Item label="Select Dates" name={'selectDates'}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Message Type" name={'messageType'}>
          {/* <Input /> */}
          <Select size="middle" placeholder={'Select message type'} style={{ minWidth: '200px' }}>
            {messageTypeList.map(type => {
              return (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        <Space style={{ float: 'right' }}>
          <Tooltip title="Clear fields">
            <Button
              type="default"
              size={'middle'}
              icon={<ClearOutlined />}
              onClick={handleRefresh}
            ></Button>
          </Tooltip>
          <Button type="primary" size="middle" htmlType="submit" key={'submit'}>
            Search
          </Button>
        </Space>
      </Form>

      <Table
        loading={loading}
        style={{ paddingTop: '1rem' }}
        columns={columns}
        dataSource={messageLogList.map(log => ({ ...log, key: log.requestId }))}
        size={'large'}
        bordered
        scroll={{
          x: 'max-content',
        }}
        pagination={{
          current: page,
          pageSize: size,
          total: totalCount,
        }}
        onChange={(pagination, filters, sorter) => {
          const formValues = {
            messageType: messageType,
            startDate: startTimestamp,
            endDate: endTimestamp,
            page: pagination.current,
            size: pagination.pageSize,
          };

          const queryString = objectToQueryString(formValues);
          navigate(`/reports/fix-message-log?` + queryString);
        }}
      />
    </>
  );
};

export default FixMessageLog;
